import api from "@/services/api";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type Notification = {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  metadata?: any;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  occurrenceId?: string;
  workOrderId?: string;
};

type NotificationsResponse = {
  data: Notification[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export const useGetNotifications = () => {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const response = await api.get<NotificationsResponse>("/notifications");
      return response.data;
    },
  });
};

export const useInfiniteNotifications = (onlyOld: boolean = false) => {
  return useInfiniteQuery({
    queryKey: ["infiniteNotifications", onlyOld],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('page', pageParam.toString());
      params.append('limit', '20');
      params.append('orderBy', 'createdAt');
      params.append('order', 'desc');
      
      if (onlyOld) {
        params.append('onlyOld', 'true');
      }
      
      const response = await api.get<NotificationsResponse>(`/notifications?${params.toString()}`);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ["markAsRead"],
    mutationFn: async (notificationIds: string[]) => {
      const response = await api.patch("/notifications/mark-as-read", {
        notificationIds,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["infiniteNotifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ["markAllAsRead"],
    mutationFn: async () => {
      const response = await api.patch("/notifications/mark-all-as-read");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["infiniteNotifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
    },
  });
};

export const useGetUnreadCount = () => {
  return useQuery({
    queryKey: ["unreadCount"],
    queryFn: async () => {
      const response = await api.get<{ count: number }>("/notifications/unread/count");
      return response.data.count;
    },
    refetchInterval: 30000, // Refetch a cada 30 segundos
  });
};
